import { CallChaining, FormFieldWidget, ModelFieldType, SPI, SubmitValue, ViewType, Widget, http } from '@kunlun/dependencies';
import { CascaderProps, message } from 'ant-design-vue';
import { cloneDeep } from 'lodash-es';
import { addressFileURL } from '../../../../utils/config';
import { queryLimitProvinceList } from '../../../../api';
import ProvinceLimit from './ProvinceLimit.vue';
import { TDeliveryAddressType, TDeliveryAreaType, TIssuingMethod } from '../../../../hooks';

@SPI.ClassFactory(
  FormFieldWidget.Token({
    viewType: ViewType.Form,
    ttype: ModelFieldType.ManyToOne,
    widget: 'ProvinceLimit',
    multi: false
  })
)
export class FormM2oProvinceLimitFieldWidget extends FormFieldWidget {
  @Widget.Reactive()
  @Widget.Inject()
  protected mountedCallChaining: CallChaining | undefined;

  @Widget.Reactive()
  private areaList: string[][] = [];

  @Widget.Reactive()
  private areaOptions: CascaderProps['options'] = [];

  private originAreaList: any[] = [];

  public initialize(props) {
    super.initialize(props);
    this.setComponent(ProvinceLimit);
    return this;
  }

  protected mounted() {
    super.mounted();
    if (this.mountedCallChaining) {
      this.mountedCallChaining.hook(this.path, async () => {
        await this.mountedProcess();
      });
    } else {
      this.mountedProcess();
    }
  }

  async updateLimitProvinceList() {
    try {
      const buyerPartnerId = this.formData.buyerPartnerId || (this.formData.buyer as any)?.id || (this.formData.distributorProxy as any)?.id;
      const sellerPartnerId = this.formData.sellerPartnerId || (this.formData.seller as any)?.id || (this.formData.originPartner as any)?.id;
      const ariesIssuingMethodEnum = (this.formData.ariesIssuingMethodEnum || this.formData.issuingMethod || this.formData.issuingMethodEnum) as TIssuingMethod;

      if (!ariesIssuingMethodEnum || !buyerPartnerId || !sellerPartnerId) {
        throw { message: 'buyerPartnerId, sellerPartnerId, ariesIssuingMethodEnum is required' };
      }

      // 【代发方式=总部直发*】【本次发货范围=全国】【配送至=其他地址】，可录入全国地址
      if (
        ['MASTER_EXPRESS', 'MASTER_CONSIGNED'].includes(ariesIssuingMethodEnum) &&
        (this.formData.deliveryAreaTypeEnum as TDeliveryAreaType) === 'ALL' &&
        (this.formData.deliveryAddressTypeEnum as TDeliveryAddressType) === 'OTHER'
      ) {
        this.areaOptions = cloneDeep(this.originAreaList);
        return;
      }

      const querySource = this.model.model == 'deli.aries.seller.SellerDistributionCreateOrderParam' ? '1' : this.model.model == 'deli.aries.seller.DeliAriesSellerPurchasingPlan' ? '2' : '3';
      const codes = await queryLimitProvinceList(this.model, {
        buyerPartnerId,
        sellerPartnerId,
        ariesIssuingMethodEnum,
        querySource
      });
      if (!codes.length) {
        return;
      }
      const interval = setInterval(async () => {
        if (this.originAreaList.length > 0) {
          clearInterval(interval);
          const arr = cloneDeep(this.originAreaList);
          arr.forEach((i) => {
            i.children = i.children!.filter((j) => codes.map((i) => i.code).includes(j.code)); // 过滤指定省份
          });
          this.areaOptions = arr;
        }
      }, 100);
    } catch (error: any) {
      console.error(error?.message);
    }
  }

  @Widget.Watch('formData.buyerPartnerId')
  private watchBuyerPartnerId(val: string, oldVal) {
    if (oldVal && val) {
      this.updateLimitProvinceList();
      this.resetValue();
    }
  }

  @Widget.Watch('formData.ariesIssuingMethodEnum')
  private watchAriesIssuingMethodEnum(val: TIssuingMethod, oldVal) {
    
    if (oldVal && val) {
      this.updateLimitProvinceList();
      this.resetValue();
    }
  }

  @Widget.Watch('formData.issuingMethod')
  private watchIssuingMethod(val: TIssuingMethod, oldVal) {
    if (oldVal && val) {
      this.updateLimitProvinceList();
      this.resetValue();
    }
  }

  @Widget.Watch('formData.issuingMethodEnum')
  private watchIssuingMethodEnum(val: TIssuingMethod, oldVal) {
    
    if (oldVal && val) {
      this.updateLimitProvinceList();
      this.resetValue();
    }
  }

  @Widget.Watch('formData.deliveryAreaTypeEnum')
  private watchDeliveryAreaTypeEnum(val: TIssuingMethod, oldVal) {
    if (oldVal && val) {
      this.updateLimitProvinceList();
      this.resetValue();
    }
  }

  @Widget.Watch('formData.deliveryAddressTypeEnum')
  private watchDeliveryAddressTypeEnum(val: TIssuingMethod, oldVal) {
    if (oldVal && val) {
      this.updateLimitProvinceList();
      this.resetValue();
    }
  }

  private resetValue() {
    this.setValue(null);
    this.areaList = [];
  }

  public async mountedProcess() {
    const res = await this.getRegionList();
    // 下钻，第四级 镇/乡/街道 不需要
    res.children.forEach((i) => {
      i?.children?.forEach((j) => {
        j?.children?.forEach((k) => {
          if (k.children) {
            k.children = [];
          }
        });
      });
    });
    this.originAreaList = [res];
    this.areaOptions = [res];

    if (this.value) {
      const { countryCode, provinceCode, cityCode, districtCode, streetCode } = this.value as any;
      this.areaList = [countryCode, provinceCode, cityCode, districtCode, streetCode];
    }
    this.updateLimitProvinceList();
  }

  private parseCascaderValue(data: any[], obj) {
    data?.forEach((i) => {
      obj[i.code] = {
        ...i,
        allCode: obj[i.pCode]?.allCode ? `${obj[i.pCode].allCode},${i.code}` : i.code
      };
      this.parseCascaderValue(i?.children ?? [], obj);
    });
  }

  async submit(submitValue: SubmitValue) {
    return super.submit(submitValue);
  }

  private async getRegionList() {
    const res = await fetch(addressFileURL, { method: 'GET' });
    const data = await res.text();
    const str = data.replace('regionTreeResolve(', '').replace(/\)/g, '');
    return JSON.parse(str)[0];
  }

  @Widget.Method()
  private onChange(value: any[], selectedOptions) {
    this.areaList = value;
    this.setValue({
      countryCode: selectedOptions[0].code,
      countryName: selectedOptions[0].name,
      provinceCode: selectedOptions[1].code,
      provinceName: selectedOptions[1].name,
      cityCode: selectedOptions[2].code,
      cityName: selectedOptions[2].name,
      districtCode: selectedOptions[3].code,
      districtName: selectedOptions[3].name,
      streetCode: selectedOptions?.[4]?.code ?? null,
      streetName: selectedOptions?.[4]?.name ?? null,
      fullAddress:
        selectedOptions[0].name +
        selectedOptions[1].name +
        selectedOptions[2].name +
        selectedOptions[3].name +
        (selectedOptions?.[4]?.name ?? '') +
        (this.formData.deliveryAddress as any)?.detailAddress
    });
  }
}
